/**
 * PostSignupPushNudge — one-tap enable-notifications prompt shown once,
 * immediately after a fresh registration. This is the "on by default" UX:
 * the user isn't forced but doesn't have to hunt in Settings either.
 *
 * Why not silently subscribe? Browsers require an explicit user gesture
 * before calling `Notification.requestPermission()`. This modal's Enable
 * button IS that gesture — clicking it chains straight into the permission
 * prompt in the same event tick, which is exactly what the spec wants.
 */
import React, { useState } from "react";
import { BellRing, X, Loader2 } from "lucide-react";
import usePushSubscription from "../lib/usePushSubscription";
import { toast } from "sonner";

export default function PostSignupPushNudge({ open, onDone }) {
  const { supported, permission, subscribe } = usePushSubscription();
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  // If the browser can't do push (Safari desktop without PWA install,
  // some in-app browsers, etc.), skip the nudge silently — the user
  // shouldn't be shown a promise we can't deliver.
  if (!supported) {
    // Fire the done callback on next tick so the parent can navigate.
    setTimeout(onDone, 0);
    return null;
  }

  // If they already denied at OS/browser level (rare on fresh signup),
  // don't nag — respect the standing choice.
  if (permission === "denied") {
    setTimeout(onDone, 0);
    return null;
  }

  const onEnable = async () => {
    if (busy) return;
    setBusy(true);
    const res = await subscribe();
    setBusy(false);
    if (res.ok) {
      toast.success("Notifications on — the archive will whisper.");
    } else if (res.reason === "denied") {
      // No toast — the browser already showed the OS prompt they declined.
    } else if (res.reason !== "default") {
      toast.error("Couldn't enable right now. You can turn it on in Settings.");
    }
    onDone?.();
  };

  const onSkip = () => {
    if (busy) return;
    onDone?.();
  };

  return (
    <div
      data-testid="post-signup-push-nudge"
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="push-nudge-title"
    >
      <div className="relative w-full max-w-md bg-card border border-border rounded-lg shadow-2xl">
        <button
          data-testid="post-signup-push-skip-x"
          onClick={onSkip}
          aria-label="Skip"
          className="absolute top-4 right-4 text-foreground hover:opacity-70 transition-opacity"
        >
          <X size={18} />
        </button>

        <div className="p-8 pt-10">
          <div className="mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent))]">Threshold</div>
          <div className="flex items-start gap-4 mt-3">
            <div className="w-11 h-11 rounded-full bg-[hsl(var(--accent)/0.15)] grid place-items-center flex-shrink-0">
              <BellRing size={18} className="text-[hsl(var(--primary))]" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 id="push-nudge-title" className="serif text-2xl text-foreground leading-tight">
                Let the archive whisper.
              </h2>
              <p className="mt-3 text-muted-foreground leading-relaxed text-[15px]">
                Get a quiet ping when someone replies to your correlation,
                follows you, or the weekly convergence arrives. No spam &mdash;
                just the traffic that&rsquo;s about you.
              </p>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3 justify-end">
            <button
              type="button"
              data-testid="post-signup-push-skip"
              onClick={onSkip}
              disabled={busy}
              className="mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors px-4 py-3"
            >
              Not now
            </button>
            <button
              type="button"
              data-testid="post-signup-push-enable"
              onClick={onEnable}
              disabled={busy}
              className="btn-gold inline-flex items-center gap-2 disabled:opacity-40"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <BellRing size={14} />}
              Enable notifications
            </button>
          </div>

          <p className="mt-5 mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/75">
            You can change this anytime in Settings.
          </p>
        </div>
      </div>
    </div>
  );
}
